import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { confirmAction } from '../utils/confirm';
import { GatewayRecord, fetchDevices } from '../services/api';
import {
    fetchOtaImages, fetchOtaDevices, uploadOtaImage, deleteOtaImage,
    startOtaUpdate, abortOtaUpdate, fetchOtaPlan,
} from '../services/otaApi';

interface OtaImage {
    fileName: string;
    fileSize: number;
    manufacturerCode: number;
    imageType: number;
    fileVersion: number;
    addedAt: string;
}

interface OtaDeviceStatus {
    ieeeAddr: string;
    currentFileVersion?: number;
    availableVersion?: number;
    state: 'idle' | 'waiting' | 'in-progress' | 'complete' | 'error';
    progress?: number;
    remainingSeconds?: number;
    lastActivity?: string;
    message?: string;
    error?: string;
}

interface Props {
    gateways: GatewayRecord[];
}

const OtaSection: React.FC<Props> = ({ gateways }) => {
    const [expanded, setExpanded] = useState(false);
    const [selectedGw, setSelectedGw] = useState<string>('');
    const [images, setImages] = useState<OtaImage[]>([]);
    const [devices, setDevices] = useState<OtaDeviceStatus[]>([]);
    const [allDevices, setAllDevices] = useState<Array<{ id: string; name: string; coordinator_id?: string | null }>>([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Memoised so the effect below can depend on it without re-running every render.
    const zigbeeGateways = useMemo(() => gateways.filter(g => g.zigbeeEnabled), [gateways]);

    useEffect(() => {
        if (zigbeeGateways.length > 0 && !selectedGw) {
            setSelectedGw(zigbeeGateways[0].id);
        }
    }, [zigbeeGateways, selectedGw]);

    const loadOtaData = useCallback(async () => {
        if (!selectedGw) return;
        try {
            const [images, otaDevices, allDevs] = await Promise.all([
                fetchOtaImages(selectedGw),
                fetchOtaDevices(selectedGw),
                fetchDevices(),
            ]);
            setImages(images as unknown as OtaImage[]);
            setDevices(otaDevices as unknown as OtaDeviceStatus[]);
            setAllDevices(allDevs.filter(d =>
                d.coordinator_id === selectedGw && d.ieee_address?.startsWith('0x')
            ));
            setError(null);
        } catch (e: any) {
            if (e.response?.status !== 401) {
                setError(e.response?.data?.message || 'Kunde inte hämta uppdateringsinformation');
            }
        }
    }, [selectedGw]);

    useEffect(() => {
        if (expanded && selectedGw) loadOtaData();
    }, [expanded, selectedGw, loadOtaData]);

    // Auto-refresh during transfers
    useEffect(() => {
        if (!expanded) return;
        const hasActive = devices.some(d => d.state === 'in-progress' || d.state === 'waiting');
        if (!hasActive) return;
        const interval = setInterval(loadOtaData, 3000);
        return () => clearInterval(interval);
    }, [expanded, devices, loadOtaData]);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setUploading(true);
        setError(null);
        try {
            await uploadOtaImage(selectedGw, file);
            await loadOtaData();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Uppladdningen misslyckades');
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = async (mfr: number, type: number) => {
        if (!confirmAction('Ta bort den här programvarufilen? Pågående överföringar avbryts.')) return;
        try {
            await deleteOtaImage(selectedGw, mfr, type);
            await loadOtaData();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Kunde inte ta bort filen');
        }
    };

    const handleStartUpdate = async (ieeeAddr: string, name: string) => {
        setError(null);
        try {
            // Visa vilken fil det gäller innan något skickas, och kräv ett extra ja om
            // filnamnet inte nämner enhetens modell (fel fil kan förstöra enheten).
            const plan = await fetchOtaPlan(selectedGw, ieeeAddr);
            if (plan.files.length === 0) {
                setError(`Det finns ingen uppladdad fil från tillverkaren av ${name}.`);
                return;
            }
            const model = plan.modelId ?? 'okänd modell';
            const fileList = plan.files
                .map(f => `• ${f.fileName} (version 0x${f.fileVersion.toString(16).padStart(8, '0')})`)
                .join('\n');
            if (!confirmAction(
                `Uppdatera programvaran i ${name} (${model}) nu?\n\n` +
                `Fil från enhetens tillverkare:\n${fileList}\n\n` +
                `Filen skickas bara om den är nyare än den enheten har. ` +
                `Det tar ofta 10–20 minuter och enheten startar om på slutet. ` +
                `En misslyckad uppdatering kan förstöra enheten.`
            )) return;
            const modelMismatch = !plan.files.some(f => f.modelInName);
            if (modelMismatch && !confirmAction(
                `VARNING: inget filnamn nämner enhetens modell "${model}".\n\n${fileList}\n\n` +
                `Samma tillverkare kan ha flera modeller som tar emot filer av samma typ. ` +
                `Fel fil kan förstöra enheten för gott.\n\n` +
                `Är du säker på att filen är gjord för just ${model}?`
            )) return;
            await startOtaUpdate(selectedGw, ieeeAddr, modelMismatch);
            await loadOtaData();
        } catch (err: any) {
            setError(err.message || 'Kunde inte starta uppdateringen');
        }
    };

    const handleAbortUpdate = async (ieeeAddr: string) => {
        if (!confirmAction('Avbryta uppdateringen? Enheten fortsätter med den programvara den har.')) return;
        try {
            await abortOtaUpdate(selectedGw, ieeeAddr);
            await loadOtaData();
        } catch (err: any) {
            setError(err.message || 'Kunde inte avbryta uppdateringen');
        }
    };

    const stateLabel = (state: string) => {
        switch (state) {
            case 'waiting': return 'väntar på enheten';
            case 'in-progress': return 'pågår';
            case 'complete': return 'klar';
            case 'error': return 'fel';
            default: return 'vilar';
        }
    };

    const stateColor = (state: string) => {
        switch (state) {
            case 'waiting':
            case 'in-progress': return '#ffa000';
            case 'complete': return '#28a745';
            case 'error': return '#dc3545';
            default: return '#888';
        }
    };

    if (zigbeeGateways.length === 0) return null;

    return (
        <div style={{ marginTop: '24px', border: '2px solid #f5c6cb', borderRadius: '6px', overflow: 'hidden' }}>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                style={{
                    width: '100%', background: '#fff3cd', border: 'none', padding: '12px 16px',
                    cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', fontSize: '14px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    color: '#856404',
                }}
            >
                <span>⚠️ Uppdatera programvara i enheter (avancerat)</span>
                <span>{expanded ? '▲' : '▼'}</span>
            </button>

            {expanded && (
                <div style={{ padding: '16px', background: '#fffef5' }}>
                    {/* Warning */}
                    <div style={{
                        background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px',
                        padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#721c24',
                    }}>
                        <strong>⚠️ Varning:</strong> fel programvara kan <strong>förstöra</strong> en enhet helt.
                        Ladda bara upp .ota-filer från källor du litar på och som passar exakt din enhetsmodell.
                        Batteridrivna enheter sover för det mesta: väck dem (tryck på knappen) precis innan du startar.
                    </div>

                    {/* Gateway selector */}
                    {zigbeeGateways.length > 1 && (
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', marginRight: '8px' }}>Samordnare:</label>
                            <select value={selectedGw} onChange={e => setSelectedGw(e.target.value)}
                                style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '4px', border: '1px solid #ccc' }}>
                                {zigbeeGateways.map(gw => (
                                    <option key={gw.id} value={gw.id}>{gw.name} ({gw.id})</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {error && (
                        <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '8px 12px', marginBottom: '12px', fontSize: '12px', color: '#721c24' }}>
                            {error}
                            <button onClick={() => setError(null)} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold' }}>×</button>
                        </div>
                    )}

                    {/* Firmware Images */}
                    <h4 style={{ margin: '0 0 8px', fontSize: '13px' }}>Uppladdade filer</h4>
                    <div style={{ marginBottom: '16px' }}>
                        {images.length === 0 ? (
                            <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px' }}>Inga filer uppladdade än.</p>
                        ) : (
                            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginBottom: '8px' }}>
                                <thead>
                                    <tr style={{ background: '#f0f0f0', textAlign: 'left' }}>
                                        <th style={{ padding: '6px 8px' }}>Fil</th>
                                        <th style={{ padding: '6px 8px' }}>Tillverkare</th>
                                        <th style={{ padding: '6px 8px' }}>Filtyp</th>
                                        <th style={{ padding: '6px 8px' }}>Version</th>
                                        <th style={{ padding: '6px 8px' }}>Storlek</th>
                                        <th style={{ padding: '6px 8px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {images.map(img => (
                                        <tr key={`${img.manufacturerCode}:${img.imageType}`} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{img.fileName}</td>
                                            <td style={{ padding: '6px 8px' }}>0x{img.manufacturerCode.toString(16).padStart(4, '0')}</td>
                                            <td style={{ padding: '6px 8px' }}>0x{img.imageType.toString(16).padStart(4, '0')}</td>
                                            <td style={{ padding: '6px 8px' }}>0x{img.fileVersion.toString(16).padStart(8, '0')}</td>
                                            <td style={{ padding: '6px 8px' }}>{(img.fileSize / 1024).toFixed(1)} KB</td>
                                            <td style={{ padding: '6px 8px' }}>
                                                <button onClick={() => handleRemoveImage(img.manufacturerCode, img.imageType)}
                                                    style={{ fontSize: '11px', color: '#dc3545', background: 'none', border: '1px solid #dc3545', borderRadius: '3px', padding: '2px 6px', cursor: 'pointer' }}>
                                                    Ta bort
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <label style={{
                            display: 'inline-block', padding: '6px 12px', fontSize: '12px', fontWeight: 'bold',
                            background: '#e2e3e5', border: '1px solid #d6d8db', borderRadius: '4px', cursor: 'pointer',
                            opacity: uploading ? 0.6 : 1,
                        }}>
                            {uploading ? 'Laddar upp…' : '⬆ Ladda upp fil (.ota)'}
                            <input type="file" accept=".ota,.ota.zigbee,.bin" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
                        </label>
                        <p style={{ fontSize: '11px', color: '#666', margin: '6px 0 0' }}>
                            Hämta filen själv från tillverkaren eller från <a href="https://github.com/Koenkk/zigbee-OTA" target="_blank" rel="noopener noreferrer">Koenkk zigbee-OTA</a> och ladda upp den här.
                        </p>
                    </div>

                    {/* Device OTA Status */}
                    <h4 style={{ margin: '0 0 8px', fontSize: '13px' }}>Uppdateringsläge per enhet</h4>
                    {allDevices.length === 0 ? (
                        <p style={{ fontSize: '12px', color: '#888' }}>Inga enheter på den här samordnaren.</p>
                    ) : (
                        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f0f0f0', textAlign: 'left' }}>
                                    <th style={{ padding: '6px 8px' }}>Enhet</th>
                                    <th style={{ padding: '6px 8px' }}>Nuvarande version</th>
                                    <th style={{ padding: '6px 8px' }}>Tillgänglig</th>
                                    <th style={{ padding: '6px 8px' }}>Läge</th>
                                    <th style={{ padding: '6px 8px' }}>Uppdatering</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allDevices.map(dev => {
                                    const status = devices.find(d => d.ieeeAddr === dev.id);
                                    const state = status?.state ?? 'idle';
                                    const running = state === 'in-progress' || state === 'waiting';
                                    return (
                                        <tr key={dev.id} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '6px 8px' }}>
                                                <span style={{ fontWeight: 'bold' }}>{dev.name}</span>
                                                <br />
                                                <span style={{ fontFamily: 'monospace', fontSize: '10px', color: '#888' }}>{dev.id}</span>
                                            </td>
                                            <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                                                {status?.currentFileVersion != null ? `0x${status.currentFileVersion.toString(16).padStart(8, '0')}` : '—'}
                                            </td>
                                            <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                                                {status?.availableVersion != null ? `0x${status.availableVersion.toString(16).padStart(8, '0')}` : '—'}
                                            </td>
                                            <td style={{ padding: '6px 8px' }}>
                                                <span style={{ color: stateColor(state), fontWeight: state !== 'idle' ? 'bold' : 'normal' }}>
                                                    {stateLabel(state)}
                                                    {state === 'in-progress' && status?.progress != null && ` (${status.progress} %)`}
                                                </span>
                                                {state === 'in-progress' && status?.remainingSeconds != null && (
                                                    <span style={{ display: 'block', fontSize: '10px', color: '#888' }}>cirka {Math.ceil(status.remainingSeconds / 60)} min kvar</span>
                                                )}
                                                {status?.message && (
                                                    <span style={{ display: 'block', fontSize: '10px', color: '#666' }}>{status.message}</span>
                                                )}
                                                {state === 'error' && status?.error && (
                                                    <span style={{ display: 'block', fontSize: '10px', color: '#dc3545' }}>{status.error}</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 8px' }}>
                                                <button
                                                    onClick={() => running ? handleAbortUpdate(dev.id) : handleStartUpdate(dev.id, dev.name)}
                                                    style={{
                                                        fontSize: '11px', padding: '3px 8px', borderRadius: '3px', cursor: 'pointer',
                                                        border: `1px solid ${running ? '#dc3545' : '#28a745'}`,
                                                        color: running ? '#dc3545' : '#28a745',
                                                        background: running ? '#fff5f5' : '#f0fff4',
                                                        fontWeight: 'bold',
                                                    }}
                                                    disabled={!running && images.length === 0}
                                                    title={!running && images.length === 0 ? 'Ladda upp en fil först' : undefined}
                                                >
                                                    {running ? 'Avbryt' : 'Uppdatera nu'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    <button onClick={loadOtaData} style={{ marginTop: '12px', fontSize: '12px', padding: '4px 10px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px', background: '#f8f9fa' }}>
                        ↻ Uppdatera
                    </button>
                </div>
            )}
        </div>
    );
};

export default OtaSection;
